import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useMutation } from 'react-query';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import ProvisioningStyles from 'pages/provisioning/alb/style';
import {
    environmentDetails,
    isBackendPoolsConfigFullfilled,
    isHealthProbeFullfilled,
    isMandatoryTagsFullfilled,
    orderId,
    quickConfigDetails,
    sspSubmitOrder,
    toggleSspModels,
} from 'pages/provisioning/store/alb';
import {
    resetEnvironmentDetails,
    resetOrder,
    resetToggleSspModal,
    formOrderObjFromCart,
    formOrderObjFromOrderDetails,
    isBpFormFilled,
    isHpFormFilled,
} from './utils';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/alb/ProvisioningHeader'));
const ApplicationDetails = React.lazy(() => import('pages/provisioning/alb/ApplicationDetails'));
const SspForm = React.lazy(() => import('pages/provisioning/alb/SspForm'));
const Tags = React.lazy(() => import('pages/provisioning/alb/Tags'));
const AddedToCartConfirmModal = React.lazy(() => import('pages/provisioning/alb/AddedToCartConfirmModal'));
const UpdatedToCartConfirmModal = React.lazy(() => import('pages/provisioning/alb/UpdatedToCartConfirmModal'));
const QuickConfig = React.lazy(() => import('pages/provisioning/alb/QuickConfig'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/alb/ShowCostDetails'));
const HealthProbeModal = React.lazy(() => import('pages/provisioning/alb/HealthProbeModal'));
const BackendPoolsModal = React.lazy(() => import('pages/provisioning/alb/BackendPoolsModal'));

const AlbProvisioningPage = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [, setIsBackendPoolsConfig] = useRecoilState(isBackendPoolsConfigFullfilled);
    const [, setIsHPConfig] = useRecoilState(isHealthProbeFullfilled);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetEnvDetails] = useRecoilState(environmentDetails);
    const [, resetIsMandatoryTagsFullfilled] = useRecoilState(isMandatoryTagsFullfilled);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [, resetQuickConfig] = useRecoilState(quickConfigDetails);
    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const axios = useAxios();

    const resourceGroupApi = useMutation((payload) => axios.get(endPoints.mySql.resourceGroup(payload)));

    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }
    async function checkCatalog() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}`);
        const costs = await getCosts();
        if (cartItemId && operation && !catalogServiceId) {
            const { data: cartItem } = await axios.get(`${endPoints.cart.getCartItem}/${cartItemId}`);
            const orderObj = formOrderObjFromCart(cartItem);
            let resourceGroupApiResults = null;
            let subscriptionDetails = orderObj?.subscriptionDetails || null;
            const location = orderObj?.region;
            const rgName = orderObj?.resourceGroup || '';
            if (rgName && location) {
                try {
                    const response = await resourceGroupApi.mutateAsync({ location, rgName });
                    resourceGroupApiResults = Array.isArray(response?.data) ? response.data : null;
                    const matched = resourceGroupApiResults?.find((item) => item.subscriptionName === orderObj?.subscription);
                    subscriptionDetails = matched || resourceGroupApiResults?.[0] || null;
                } catch (error) {
                    resourceGroupApiResults = null;
                }
            }
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                resourceGroupDetails: resourceGroupApiResults,
                subscription: subscriptionDetails?.subscriptionName || null,
                subscriptionDetails,
                emptyCost: costs,
            }));
            setIsBackendPoolsConfig(isBpFormFilled({ order }));
            setIsHPConfig(isHpFormFilled({ order }));
        }
        if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetails}/${cartItemId}`);
            const orderObj = formOrderObjFromOrderDetails(catalogServiceId, orderDetails);
            let resourceGroupApiResults = null;
            let subscriptionDetails = orderObj?.subscriptionDetails || null;
            const location = orderObj?.region;
            const rgName = orderObj?.resourceGroup || '';
            if (rgName && location) {
                try {
                    const response = await resourceGroupApi.mutateAsync({ location, rgName });
                    resourceGroupApiResults = Array.isArray(response?.data) ? response.data : null;
                    const matched = resourceGroupApiResults?.find((item) => item.subscriptionName === orderObj?.subscription);
                    subscriptionDetails = matched || resourceGroupApiResults?.[0] || null;
                } catch (error) {
                    resourceGroupApiResults = null;
                }
            }
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                resourceGroupDetails: resourceGroupApiResults,
                subscription: subscriptionDetails?.subscriptionName || null,
                subscriptionDetails,
            }));
            setIsBackendPoolsConfig(isBpFormFilled({ order }));
            setIsHPConfig(isHpFormFilled({ order }));
        } else {
            setOrder((previous) => ({
                ...previous,
                sku: data?.images?.sku || previous?.sku,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }

    const reset = () => {
        setOrder({ ...order, ...resetOrder });
        setIsBackendPoolsConfig(false);
        setIsHPConfig(false);
        resetOrderId('');
        resetEnvDetails(resetEnvironmentDetails);
        resetIsMandatoryTagsFullfilled(false);
        resetToggleSspModels(resetToggleSspModal);
        resetQuickConfig(undefined);
    };

    React.useEffect(() => {
        if (catalogCode) {
            setLoading(true);
            reset();
            checkCatalog();
        }
    }, [catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <Helmet>
                <title>ALB Provisioning - DevX Infra</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <ApplicationDetails />
                        <Tags />
                        <AddedToCartConfirmModal />
                        <UpdatedToCartConfirmModal />
                        <QuickConfig />
                        <ShowCostDetails />
                        <HealthProbeModal />
                        <BackendPoolsModal />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default AlbProvisioningPage;
